/* eslint-disable react-hooks/set-state-in-effect */
/**
 * StudioSingleShot
 *
 * Single Shot Studio - Generation UI mit Master Image System
 * - 3-column layout: Asset & Context | Image Preview | Generation Settings
 * - Product preselection via URL params
 * - Gallery below
 *
 * Route: /v2/tools/studio/single-shot
 * @accessibility WCAG 2.1 AA + BFSG konform
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ImageIcon, Settings, Upload } from 'lucide-react';
import { useSimpleGallery } from '@/hooks/useGalleryV2';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { galleryImagesToLegacy } from '@/lib/api/gallery-v2';
import { GenerationFormV2 } from '@/components/v2/tools/generation/GenerationFormV2';
import { MasterImageTabs } from '@/components/v2/tools/generation/MasterImageTabs';
import { AssetContextPanelV2 } from '@/components/v2/tools/generation/AssetContextPanelV2';
import { ImagePreviewPanel } from '@/components/v2/tools/generation/ImagePreviewPanel';

import { type GeneratedImage } from '@/components/studio';
import { StudioAddToProductBridge } from './StudioAddToProductBridge';
import type { Image } from '@/types';
import type { ReferenceSlot } from '@/components/v2/tools/generation/GenerationFormV2';

// Panel size configuration
const LEFT_PANEL_WIDTH_PERCENT = 0.21; // 21% of viewport width (~400px on Full HD)
const MIN_LEFT_PANEL_WIDTH = 280;
const MAX_LEFT_PANEL_WIDTH = 450;

const RIGHT_PANEL_WIDTH_PERCENT = 0.20; // 20% of viewport width
const MIN_RIGHT_PANEL_WIDTH = 320;
const MAX_RIGHT_PANEL_WIDTH = 480;

// Calculate initial panel widths based on viewport
const getInitialLeftPanelWidth = () => {
  const viewportWidth = window.innerWidth;
  const calculatedWidth = viewportWidth * LEFT_PANEL_WIDTH_PERCENT;
  return Math.min(Math.max(calculatedWidth, MIN_LEFT_PANEL_WIDTH), MAX_LEFT_PANEL_WIDTH);
};

const getInitialRightPanelWidth = () => {
  const viewportWidth = window.innerWidth;
  const calculatedWidth = viewportWidth * RIGHT_PANEL_WIDTH_PERCENT;
  return Math.min(Math.max(calculatedWidth, MIN_RIGHT_PANEL_WIDTH), MAX_RIGHT_PANEL_WIDTH);
};

export function StudioSingleShot() {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [mobilePanel, setMobilePanel] = useState<'assets' | 'preview' | 'settings'>('settings');
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(12);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStartTime, setGenerationStartTime] = useState<Date | undefined>(undefined);

  // Master Image System State
  const [activeTab] = useState<'single-shot'>('single-shot');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageInfo, setImageInfo] = useState<{ width: number; height: number; format: string; size: number } | null>(null);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);

  // Read URL parameters for product preselection
  const productIdFromUrl = searchParams.get('productId');
  const tabFromUrl = searchParams.get('tab') as 'upload' | 'gallery' | 'product' | null;

  const [leftPanelTab, setLeftPanelTab] = useState<'upload' | 'gallery' | 'product'>(
    tabFromUrl || 'upload'
  );
  const [generatedImage, setGeneratedImage] = useState<Image | null>(null);

  // Product Assignment SlideOver State
  const [assignmentSlideOverOpen, setAssignmentSlideOverOpen] = useState(false);
  const [generatedImagesForAssignment, setGeneratedImagesForAssignment] = useState<GeneratedImage[]>([]);
  const [selectedProductForAssignment, setSelectedProductForAssignment] = useState<{
    id: string;
    name: string;
    sku?: string;
  } | null>(null);

  // Reference images state (lifted for cross-panel sharing)
  const EMPTY_REF_SLOTS: ReferenceSlot[] = [
    { id: null, previewUrl: null }, { id: null, previewUrl: null },
    { id: null, previewUrl: null }, { id: null, previewUrl: null },
  ];
  const [referenceSlots, setReferenceSlots] = useState<ReferenceSlot[]>(EMPTY_REF_SLOTS);
  const [maxReferenceImages, setMaxReferenceImages] = useState(0);
  const [pendingMention, setPendingMention] = useState<string | null>(null);

  // Track selected product from left panel (for "zu Produkt hinzufügen")
  const [selectedProductFromPanel, setSelectedProductFromPanel] = useState<{
    id: string;
    name: string;
    sku?: string;
  } | null>(productIdFromUrl ? { id: productIdFromUrl, name: 'Produkt', sku: undefined } : null);

  // Resizable Panel State (dynamic based on viewport)
  const [leftPanelWidth, setLeftPanelWidth] = useState(getInitialLeftPanelWidth());
  const [rightPanelWidth, setRightPanelWidth] = useState(getInitialRightPanelWidth());
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(getInitialLeftPanelWidth());

  // Adjust panel widths on window resize (only if not manually resized)
  useEffect(() => {
    const handleResize = () => {
      // Only auto-adjust if user hasn't manually resized
      if (!isResizing) {
        const newLeftWidth = getInitialLeftPanelWidth();
        const newRightWidth = getInitialRightPanelWidth();
        setLeftPanelWidth(newLeftWidth);
        setRightPanelWidth(newRightWidth);
        resizeStartWidth.current = newLeftWidth;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isResizing]);

  // URL-Parameter für "Alternative erstellen" (generating=true)
  useEffect(() => {
    if (searchParams.get('generating') === 'true') {
      setIsGenerating(true);
      setCurrentPage(1);
      setGenerationStartTime(new Date());
      // Parameter entfernen nach Verarbeitung
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('generating');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // URL-Parameter für Produkt-Vorauswahl (productId + tab)
  useEffect(() => {
    if (productIdFromUrl && tabFromUrl === 'product') {
      setLeftPanelTab('product');
      // Clean up URL parameters after processing
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('productId');
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, [productIdFromUrl, tabFromUrl, searchParams, setSearchParams]);

  const { images: galleryImages, refetch } = useSimpleGallery({
    page: currentPage,
    perPage,
  });

  const handleGenerationStart = useCallback(() => {
    setIsGenerating(true);
    setCurrentPage(1);
    setGenerationStartTime(new Date());
    setGeneratedImage(null); // Clear previous generated image
  }, []);

  const handleGenerationSuccess = useCallback(async () => {
    console.log('[Experimental] Generation success, refetching gallery...');
    setIsGenerating(false);
    setGenerationStartTime(undefined);

    // Refetch gallery und hole das neueste Bild (das gerade generierte)
    const result = await refetch();
    console.log('[Experimental] Refetch result:', result);

    // React Query result format: { data: { images: [...], pagination: {...} } }
    if (result.data?.images && result.data?.images.length > 0) {
      console.log('[Experimental] Found images:', result.data?.images.length);
      const legacyImages = galleryImagesToLegacy(result.data?.images);
      if (legacyImages.length > 0) {
        console.log('[Experimental] Setting generated image:', legacyImages[0]);
        setGeneratedImage(legacyImages[0]); // Erstes Bild = neuestes

        // Add to generated images for assignment (collect all session images)
        const newImage = legacyImages[0];
        if (newImage.asset_id) {
          setGeneratedImagesForAssignment(prev => {
            // Avoid duplicates
            if (prev.some(img => img.assetId === newImage.asset_id)) {
              return prev;
            }
            return [...prev, {
              assetId: newImage.asset_id!,
              s3Url: newImage.image_url,
              width: newImage.width || 1024,
              height: newImage.height || 1024,
              generatedAt: new Date(),
            }];
          });
        }
      }
    } else {
      console.warn('[Experimental] No images found after refetch');
    }
  }, [refetch]);

  const handleGenerationError = useCallback(() => {
    setIsGenerating(false);
    setGenerationStartTime(undefined);
  }, []);

  // Handle "zu Produkt hinzufügen" - opens assignment slide-over
  const handleAddToProduct = useCallback((productId: string, productName: string, productSku?: string) => {
    if (generatedImagesForAssignment.length === 0) {
      console.warn('No generated images to assign');
      return;
    }
    setSelectedProductForAssignment({ id: productId, name: productName, sku: productSku });
    setAssignmentSlideOverOpen(true);
  }, [generatedImagesForAssignment]);

  // Handle assignment complete - clear generated images
  const handleAssignmentComplete = useCallback(() => {
    setGeneratedImagesForAssignment([]);
    setSelectedProductForAssignment(null);
  }, []);

  // Wrapper for "zu Produkt hinzufügen" button click (from ImagePreviewPanel)
  const handleAddToProductClick = useCallback(() => {
    if (generatedImagesForAssignment.length === 0) {
      alert('Bitte generiere zuerst ein Bild, bevor du es zu einem Produkt hinzufügst.');
      return;
    }
    // If product is selected (from URL or product tab)
    if (selectedProductFromPanel) {
      handleAddToProduct(
        selectedProductFromPanel.id,
        selectedProductFromPanel.name,
        selectedProductFromPanel.sku
      );
    } else {
      // No product selected - prompt user
      alert('Bitte wähle zuerst ein Produkt im linken Panel aus (Tab "Produkt")');
    }
  }, [generatedImagesForAssignment.length, selectedProductFromPanel, handleAddToProduct]);

  // Resize Handler
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = leftPanelWidth;
    setIsResizing(true);
  }, [leftPanelWidth]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = e.clientX - resizeStartX.current;
      const newWidth = resizeStartWidth.current + delta;
      const constrainedWidth = Math.min(Math.max(newWidth, MIN_LEFT_PANEL_WIDTH), MAX_LEFT_PANEL_WIDTH);
      setLeftPanelWidth(constrainedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  // Handle file upload (from Upload tab, Gallery tab, or Product tab)
  const handleFileChange = useCallback((file: File | null) => {
    console.log('[SingleImageMode] handleFileChange called with file:', file ? `${file.name} (${file.size} bytes)` : 'null');
    setSelectedFile(file);

    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      console.log('[SingleImageMode] Preview URL created:', previewUrl);
      setPreview(previewUrl);

      // Extract image info
      const img = new Image();
      img.onload = () => {
        console.log('[SingleImageMode] Image loaded:', img.width, 'x', img.height);
        setImageInfo({
          width: img.width,
          height: img.height,
          format: file.type.split('/')[1].toUpperCase(),
          size: file.size,
        });
      };
      img.src = previewUrl;
    } else {
      console.log('[SingleImageMode] Clearing preview');
      setPreview(null);
      setImageInfo(null);
    }
  }, []);

  // Handle image click from bottom gallery - switch to gallery tab and load image
  const handleBottomGalleryImageClick = useCallback(async (imageId: string) => {
    // Switch left panel to gallery tab
    setLeftPanelTab('gallery');

    // Find the image in the gallery (from legacy format)
    const legacyImages = galleryImagesToLegacy(galleryImages);
    const image = legacyImages.find(img => img.id === imageId);
    if (!image || !image.image_url) {
      console.error('Image not found:', imageId);
      return;
    }

    try {
      // Fetch image and convert to File
      const response = await fetch(image.image_url);
      const blob = await response.blob();
      const filename = `gallery-${imageId}.jpg`;
      const file = new File([blob], filename, { type: blob.type });

      handleFileChange(file);
    } catch (error) {
      console.error('Error loading gallery image:', error);
    }
  }, [galleryImages, handleFileChange]);

  const assetPanel = (
    <AssetContextPanelV2
      selectedFile={selectedFile}
      preview={preview}
      onFileChange={handleFileChange}
      selectedBrandId={selectedBrandId}
      onBrandChange={setSelectedBrandId}
      activeTab={leftPanelTab}
      onTabChange={setLeftPanelTab}
      preselectedProductId={productIdFromUrl}
      onProductSelect={setSelectedProductFromPanel}
      referenceSlots={referenceSlots}
      onReferenceSlotsChange={setReferenceSlots}
      maxReferenceImages={maxReferenceImages}
      onInsertMention={setPendingMention}
    />
  );

  const previewPanel = (
    <ImagePreviewPanel
      preview={preview}
      imageInfo={imageInfo}
      isGenerating={isGenerating}
      generationStartTime={generationStartTime}
      generatedImage={generatedImage}
      onNewGeneration={handleGenerationStart}
      onAddToProduct={handleAddToProductClick}
      onGalleryImageClick={handleBottomGalleryImageClick}
    />
  );

  const settingsPanel = (
    <GenerationFormV2
      onGenerationStart={handleGenerationStart}
      onSuccess={handleGenerationSuccess}
      onError={handleGenerationError}
      hideImageUpload={true}
      hideBrandSelector={true}
      externalBrandId={selectedBrandId}
      externalFile={selectedFile}
      externalPreview={preview}
      hasGeneratedImage={!!generatedImage}
      externalReferenceSlots={referenceSlots}
      onMaxReferenceImagesChange={setMaxReferenceImages}
      pendingMention={pendingMention}
      onMentionConsumed={() => setPendingMention(null)}
      sourceImageId={searchParams.get('sourceImageId') || undefined}
      initialRatio={searchParams.get('ratio') || undefined}
      initialPrompt={searchParams.get('prompt') || undefined}
      initialTemplateId={searchParams.get('templateId') || undefined}
    />
  );

  const assignmentOverlay = selectedProductForAssignment && (
    <StudioAddToProductBridge
      open={assignmentSlideOverOpen}
      product={selectedProductForAssignment}
      // g.assetId is generated_images.id (the 'assetId' field name is a historical misnomer — PR0 Annahme 3)
      images={generatedImagesForAssignment.map(g => ({
        id: g.assetId,
        s3Url: g.s3Url,
        width: g.width,
        height: g.height,
      }))}
      onClose={() => setAssignmentSlideOverOpen(false)}
      onComplete={handleAssignmentComplete}
    />
  );

  /* ---- Mobile Layout ---- */
  if (!isDesktop) {
    const MOBILE_TABS = [
      { id: 'settings' as const, label: 'Generieren', icon: Settings },
      { id: 'assets' as const, label: 'Assets', icon: Upload },
      { id: 'preview' as const, label: 'Vorschau', icon: ImageIcon },
    ];

    return (
      <>
        <div className="flex flex-col h-[calc(100vh-56px-64px)]">
          {/* Mode tabs */}
          <MasterImageTabs activeTab={activeTab} onTabChange={() => {}} />

          {/* Mobile panel tabs */}
          <div className="flex border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900">
            {MOBILE_TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setMobilePanel(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-colors border-b-2 -mb-px ${
                  mobilePanel === id
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-neutral-500 dark:text-neutral-400'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          {/* Active panel */}
          <div className="flex-1 overflow-y-auto">
            {mobilePanel === 'assets' && assetPanel}
            {mobilePanel === 'preview' && previewPanel}
            {mobilePanel === 'settings' && (
              <div className="bg-neutral-50 dark:bg-neutral-900/50">
                {settingsPanel}
              </div>
            )}
          </div>
        </div>
        {assignmentOverlay}
      </>
    );
  }

  /* ---- Desktop Layout ---- */
  return (
    <>
      <div id="studio-workspace" className="flex flex-col h-[98%]">
        <div id="mode-tabs" className="mode-tabs-section">
          <MasterImageTabs activeTab={activeTab} onTabChange={() => {}} />
        </div>

        <div id="generation-workspace" className="generation-workspace flex flex-1 overflow-hidden">
          <aside
            id="asset-context-panel"
            className="asset-context-panel flex-shrink-0 border-r border-neutral-200 dark:border-neutral-800 relative"
            style={{ width: `${leftPanelWidth}px` }}
          >
            {assetPanel}
            <div
              className={`absolute top-0 right-0 bottom-0 w-1 cursor-col-resize hover:bg-orange-500 transition-colors ${
                isResizing ? 'bg-orange-500' : 'bg-transparent'
              }`}
              onMouseDown={handleResizeStart}
              title="Breite anpassen"
            />
          </aside>

          <section id="image-preview-panel" className="image-preview-panel flex-1 border-r border-neutral-200 dark:border-neutral-800">
            {previewPanel}
          </section>

          <aside
            id="generation-settings-panel"
            className="generation-settings-panel flex-shrink-0 border-l border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900/50 overflow-y-auto"
            style={{ width: `${rightPanelWidth}px` }}
          >
            {settingsPanel}
          </aside>
        </div>
      </div>

      {assignmentOverlay}
    </>
  );
}

export default StudioSingleShot;
